<lo-page-header
	title="Credits"
	subtitle="One balance for every metered action across Lolly — creator lookups, AI search, outreach and the assistant all draw on it."
>
	<!-- Buying credits charges the ACCOUNT's card, so it is the owner's control
	     and a member is not shown it. Their own balance and their own statement
	     below are theirs, and stay. -->
	@if (canManageBilling) {
	<div loActions class="head-action">
		<button
			lo-button
			type="button"
			data-testid="credits-add"
			[disabled]="!packs.length"
			(click)="openAddCredits()"
		>
			Add credits
		</button>
		@if (packsLoaded && !packs.length) {
		<p class="lo-type-text-xs head-note">
			We could not read the credit blocks on offer. Reload the page to try
			again.
		</p>
		}
	</div>
	}
	<!-- A member cannot buy, so the same place offers to ask the master
	     account instead — and, once asked, says when. -->
	@if (!canManageBilling && canAsk) {
	<div loActions class="head-action">
		@if (!askedNote) {
		<button
			lo-button
			type="button"
			data-testid="credits-ask"
			[style.visibility]="requestLoaded ? null : 'hidden'"
			[loading]="askInFlight"
			[disabled]="!requestLoaded"
			(click)="ask()"
		>
			{{ askLabel }}
		</button>
		} @else {
		<p class="lo-type-text-xs-medium head-note" data-testid="credits-asked">
			{{ askedNote }}
		</p>
		}
	</div>
	}
</lo-page-header>

<div class="content">
	<!-- A failed read is not an empty balance, so it gets its own state and never
	     renders as zero. -->
	@if (failed) {
	<lo-notice
		tone="warning"
		data-testid="credits-unavailable"
		heading="We could not read your credit balance"
		body="The statement did not answer. Try again before assuming there is nothing on it."
	>
		<button
			lo-button
			variant="secondary"
			size="sm"
			type="button"
			(click)="retry()"
		>
			Try again
		</button>
	</lo-notice>
	}

	<!-- ───────── The balance ─────────
	     One figure answers one question, and the arithmetic under it names the
	     numbers it is NOT. -->
	@if (!failed) {
	<lo-card class="balance" data-testid="credits-balance">
		<div class="balance__main">
			<div
				class="balance__answer"
				[attr.aria-busy]="loading && !availableCredits"
			>
				<span class="lo-type-text-sm-medium muted">
					{{ availableLabel }}
				</span>

				@if (loading && !availableCredits) {
				<lo-skeleton
					variant="block"
					height="calc(var(--lo-space-card) * 2)"
				></lo-skeleton>
				} @if (availableCredits) {
				<div class="balance__figure">
					<span
						class="lo-type-display"
						data-testid="credits-available"
					>
						{{ availableCredits }}
					</span>
					<span class="lo-type-text-md muted">credits</span>
				</div>
				}

				<!-- The deductions, shown only when there is a gap to explain. Two
					     equal numbers in a row is what made this page read as two
					     balances, so when nothing is held back nothing is printed. -->
				@if (ledgerTerms.length) {
				<div class="ledger">
					@for (term of ledgerTerms; track term.label) { @if
					(term.deduction) {
					<span class="lo-type-text-md muted" aria-hidden="true">
						−
					</span>
					}
					<div class="ledger__term">
						<span class="lo-type-text-md-semibold">
							{{ term.value }}
						</span>
						<span class="lo-type-text-xs muted">
							{{ term.label }}
						</span>
					</div>
					}
				</div>
				}

				<!-- A purchase is accepted here and granted by a webhook a moment
					     later, so the page says so from the instant the dialog closes
					     rather than showing an unexplained old figure. -->
				@if (settling && !settleQuiet) {
				<lo-notice
					data-testid="credits-settling"
					heading="Payment accepted — your credits land here in a moment."
				></lo-notice>
				} @if (settledNote) {
				<lo-notice
					data-testid="credits-settled"
					[heading]="settledNote"
				></lo-notice>
				} @if (settleSlow) {
				<lo-notice
					tone="warning"
					data-testid="credits-settle-slow"
					heading="Your payment went through and the credits have not landed yet."
					body="Nothing was charged twice — they appear here as soon as it clears."
				>
					<button
						lo-button
						variant="secondary"
						size="sm"
						type="button"
						data-testid="credits-settle-recheck"
						(click)="checkAgain()"
					>
						Check again
					</button>
				</lo-notice>
				}
			</div>

			<div class="balance__lifetime">
				<span class="lo-type-text-sm-medium muted">Lifetime</span>
				@if (lifetimeNote) {
				<p
					class="lifetime__note"
					data-testid="credits-expiring"
					[class.lo-type-text-sm-semibold]="lifetimeUrgent"
					[class.lo-type-text-sm]="!lifetimeUrgent"
					[class.lifetime__note--urgent]="lifetimeUrgent"
				>
					{{ lifetimeNote }}
				</p>
				}
				<p class="lo-type-text-sm muted">{{ lifetimePolicyNote }}</p>
			</div>
		</div>

		<!-- Automatic top-up sits INSIDE the balance for the same reason the free
			     ceiling below does: it is what keeps the figure above off zero, not a
			     separate setting elsewhere. It renders for EVERY brand — the endpoint
			     answers every brand, and an unconfigured one reads back disabled — which
			     is the whole of what was wrong with its old home on the Billing page,
			     inside a block only Creator Data API subscribers ever saw. -->
		<div
			class="auto-topup"
			data-testid="auto-recharge"
			[anaSetting]="'credits.auto_top_up'"
		>
			@if (autoRechargeLoading) {
			<lo-skeleton variant="row"></lo-skeleton>
			} @if (autoRecharge; as auto) {
			<lo-status [tone]="auto.enabled ? 'success' : 'neutral'">
				{{ auto.enabled ? 'ON' : 'OFF' }}
			</lo-status>
			<div class="auto-topup__copy">
				<h3 class="lo-type-text-md-semibold">Automatic top-up</h3>
				<p class="lo-type-text-sm muted">{{ autoRechargeSummary }}</p>
				@if (autoRechargePausedReason) {
				<p
					class="lo-type-text-sm paused"
					data-testid="auto-recharge-paused"
				>
					Paused because {{ autoRechargePausedReason }}.
				</p>
				}
			</div>
			@if (canManageBilling) {
			<button
				lo-button
				variant="secondary"
				size="sm"
				type="button"
				data-testid="manage-auto-top-up"
				(click)="openAutoTopUp()"
			>
				Manage
			</button>
			} }
		</div>

		<!-- The free ceiling sits INSIDE the balance because it is what governs the
			     figure above, not a separate fact beside it. -->
		@if (allowanceVisible) {
		<div class="allowance" data-testid="credits-free-allowance">
			<div class="allowance__copy">
				<h3 class="lo-type-text-md-semibold">{{ allowanceTitle }}</h3>
				<p class="lo-type-text-sm muted">{{ allowanceBody }}</p>

				<!-- The allowance in terms the brand can act on, off the published
						     prices. Absent whenever no price is published: an action nobody
						     is charging for yet is not one the allowance buys more of. -->
				@if (allowanceTerms.length) {
				<div
					class="allowance__terms"
					data-testid="credits-allowance-terms"
				>
					<span class="lo-type-text-sm muted">
						What is left covers about
					</span>
					@for (term of allowanceTerms; track term.label) {
					<span class="allowance__term">
						<span class="lo-type-text-sm-semibold">
							{{ term.count }}
						</span>
						<span class="lo-type-text-sm muted">
							{{ term.label }}
						</span>
					</span>
					}
				</div>
				} @if (allowanceTermsNote) {
				<p
					class="lo-type-text-sm-semibold"
					data-testid="credits-allowance-estimate"
				>
					{{ allowanceTermsNote }}
				</p>
				} @if (allowanceUnboundedNote) {
				<p class="lo-type-text-sm muted">
					Reason on record: {{ allowanceUnboundedNote }}
				</p>
				}
			</div>

			@if (!allowanceUnboundedNote) {
			<div class="allowance__side">
				<lo-progress
					label="Free allowance used"
					[value]="allowancePercent"
				></lo-progress>
				@if (canManageBilling) {
				<button
					lo-button
					type="button"
					data-testid="credits-allowance-upgrade"
					(click)="upgrade()"
				>
					See plans
				</button>
				}
			</div>
			}
		</div>
		}
	</lo-card>
	}

	<!-- ───────── A purchase the bank never confirmed ───────── -->
	@if (outstanding.length) {
	<section class="outstanding" data-testid="credits-awaiting-authentication">
		<lo-notice
			tone="warning"
			heading="Waiting on your bank"
			body="These payments asked for extra authentication and never came back. Nothing has been charged and no credits have been added."
		></lo-notice>
		@for (purchase of outstanding; track purchase.id) {
		<div class="outstanding__row">
			<div class="outstanding__copy">
				<span class="lo-type-text-md-semibold">
					{{ purchase.amount }} · {{ purchase.credits }} credits
				</span>
				<span class="lo-type-text-sm muted">
					Started {{ purchase.started }}. We stop waiting for it on
					{{ purchase.closes }} and close it — if your bank confirms
					it before then, the credits still land.
				</span>
			</div>
			@if (canManageBilling) {
			<button
				lo-button
				variant="secondary"
				size="sm"
				type="button"
				data-testid="credits-awaiting-retry"
				[disabled]="!packs.length"
				(click)="openAddCredits(purchase.amountCents)"
			>
				Start a new checkout
			</button>
			}
		</div>
		}
	</section>
	}

	<!-- ───────── Where the credits went ───────── -->
	@if (areaRows.length) {
	<lo-card
		heading="Where your credits went"
		[headingLevel]="2"
		data-testid="credits-by-area"
	>
		<p class="lo-type-text-sm muted">
			{{ pageSpendCredits }} credits spent · {{ spendScopeNote }}
		</p>
		<div class="areas__grid">
			@for (area of areaRows; track area.department) {
			<div class="area">
				<div class="area__line">
					<span class="lo-type-text-md">{{ area.area }}</span>
					<span class="lo-type-text-md-semibold">
						{{ area.credits }}
					</span>
				</div>
				<lo-progress
					[label]="area.area + ': ' + area.credits + ' credits'"
					[value]="area.barPercent"
				></lo-progress>
			</div>
			}
		</div>
	</lo-card>
	}

	<!-- ───────── Every entry behind the balance ───────── -->
	@if (!failed) {
	<lo-card heading="Statement" [headingLevel]="2" class="entries">
		<p class="lo-type-text-sm muted">
			Every grant, purchase and spend behind the balance, newest first.
		</p>

		<lo-table
			caption="Credit statement"
			[columns]="columns"
			[rows]="rows"
			[loading]="loading"
			[loadingRows]="8"
			emptyHeading="Nothing has been granted, bought or spent yet."
		>
			<ng-template loTableCell="when" let-row>
				<span [loTooltip]="row.whenExact">{{ row.when }}</span>
			</ng-template>
			<ng-template loTableCell="label" let-row>
				<span class="entry__what">
					<span class="lo-table__truncate" [loTooltip]="row.tooltip">
						{{ row.label }}
					</span>
					@if (row.detail) {
					<span
						class="lo-table__sub lo-table__truncate"
						data-testid="credit-entry-detail"
						[loTooltip]="row.detail"
					>
						{{ row.detail }}
					</span>
					}
				</span>
			</ng-template>
			<ng-template loTableCell="amount" let-row>
				<span
					class="lo-type-text-md-semibold"
					[class.spend]="row.negative"
					[class.gain]="!row.negative"
				>
					{{ row.amount }}
				</span>
			</ng-template>
			<div loTableDock>
				<lo-pagination
					[total]="totalEntries"
					[page]="page"
					[pageSize]="pageSize"
					[pageSizes]="pageSizes"
					label="Statement pages"
					(pageChange)="onPage($event)"
					(pageSizeChange)="onPageSize($event)"
				></lo-pagination>
			</div>
		</lo-table>
	</lo-card>
	}
</div>
